import React, { useState, useEffect } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane, Row, Col } from "reactstrap";
import classnames from "classnames";
import { AUTOMATION_CATEGORIES, TEMPLATE_VARIABLES } from "../../../../models/automation";
import { NeoCard, NeoButton, NeoInput } from "../../../../components/Common";
import AutomationLogModal from "./AutomationLogModal";

/**
 * AutomationDetail
 * Painel direito do Master-Detail.
 * Tabs por gatilho na categoria selecionada, com formulário inline.
 */
const AutomationDetail = ({ triggers, categoryId, onToggleActive, onSave, isSaving }) => {

    const catInfo = AUTOMATION_CATEGORIES.find(c => c.id === categoryId) || { icon: "mdi-cog", color: "#6c757d" };
    const categoryTriggers = triggers.filter(t => t.category === categoryId);

    const [activeTabId, setActiveTabId] = useState("");
    const [formData, setFormData] = useState({});
    const [logModalOpen, setLogModalOpen] = useState(false);
    const [logTriggerId, setLogTriggerId] = useState("");
    const [logTriggerLabel, setLogTriggerLabel] = useState("");

    // Selecionar primeira tab ao mudar de categoria
    useEffect(() => {
        if (categoryTriggers.length > 0) {
            const firstId = categoryTriggers[0].id;
            setActiveTabId(firstId);
            initForm(categoryTriggers[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    const initForm = (trigger) => {
        setFormData({
            triggerId: trigger.id,
            docId: trigger.docId || null,
            name: trigger.name || trigger.label,
            channel: trigger.channel || "whatsapp",
            message: trigger.message || "",
            delayDays: trigger.delayDays || 0,
            delayHours: trigger.delayHours || 0,
            scheduledTime: trigger.scheduledTime || "08:00",
            active: trigger.active || false,
        });
    };

    const handleTabClick = (trigger) => {
        setActiveTabId(trigger.id);
        initForm(trigger);
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const insertVariable = (variable) => {
        const textarea = document.getElementById("automation-message");
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const current = formData.message || "";
        const newValue = current.substring(0, start) + variable + current.substring(end);
        handleFieldChange("message", newValue);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + variable.length, start + variable.length);
        }, 0);
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    const activeTrigger = categoryTriggers.find(t => t.id === activeTabId);

    if (!categoryId) {
        return (
            <NeoCard className="h-100 overflow-hidden">
                <div className="d-flex align-items-center justify-content-center flex-grow-1" style={{ minHeight: '400px' }}>
                    <div className="text-center text-muted">
                        <i className="mdi mdi-arrow-left-bold font-size-48 opacity-25 mb-3 d-block"></i>
                        <p className="font-size-14 mb-0 fw-semibold">Selecione uma categoria para ver os gatilhos.</p>
                    </div>
                </div>
            </NeoCard>
        );
    }

    return (
        <>
            <NeoCard className="h-100 overflow-hidden d-flex flex-column">
                {/* Header da categoria */}
                <div className="border-bottom py-3 px-4 flex-shrink-0" style={{ borderBottom: "2px solid rgba(72, 39, 18, 0.12)" }}>
                    <div className="d-flex align-items-center">
                        <div className="neo-section-icon-circle flex-shrink-0">
                            <i className={`mdi ${catInfo.icon} font-size-18 text-accent`} />
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h6 className="mb-0 fw-bold text-dark text-truncate neo-font-outfit font-size-15">{categoryId}</h6>
                            <p className="text-muted small mb-0 text-truncate">
                                {categoryTriggers.filter(t => t.active).length} de {categoryTriggers.length} ativa{categoryTriggers.length !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Tabs de gatilhos */}
                <div className="border-bottom flex-shrink-0">
                    <Nav tabs className="status-tabs-nav px-3 pt-2 border-bottom-0 flex-nowrap overflow-auto" style={{ scrollbarWidth: 'none' }}>
                        {categoryTriggers.map(trigger => (
                            <NavItem key={trigger.id}>
                                <NavLink
                                    className={classnames(
                                        'status-tab-link border-0 d-flex align-items-center gap-2',
                                        { 'active': activeTabId === trigger.id }
                                    )}
                                    onClick={() => handleTabClick(trigger)}
                                    style={{
                                        whiteSpace: 'nowrap',
                                        paddingBottom: '8px'
                                    }}
                                >
                                    <i className={`mdi ${trigger.icon} font-size-15`}></i>
                                    <span>{trigger.label}</span>
                                    {trigger.active && (
                                        <span className="ms-1 rounded-circle d-inline-block shadow-sm" style={{ width: '6px', height: '6px', backgroundColor: '#2e7d32' }}></span>
                                    )}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                </div>

                {/* Conteúdo da tab */}
                <TabContent activeTab={activeTabId} className="p-4 flex-grow-1 overflow-auto custom-scrollbar bg-white">
                    {activeTrigger && (
                        <TabPane tabId={activeTabId}>
                            {/* Descrição do gatilho */}
                            <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom" style={{ borderBottom: "1.5px solid rgba(72,39,18,0.10) !important" }}>
                                <div className="d-flex align-items-center overflow-hidden me-2">
                                    <div className="neo-section-icon-circle flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                                        <i className={`mdi ${activeTrigger.icon} font-size-20 text-accent`}></i>
                                    </div>
                                    <div className="ms-3 overflow-hidden">
                                        <h6 className="mb-0 fw-bold text-dark neo-font-outfit font-size-14">{activeTrigger.label}</h6>
                                        <p className="text-muted small mb-0 text-truncate">{activeTrigger.description}</p>
                                    </div>
                                </div>

                                {/* Switch ativar/desativar + Botão Log */}
                                <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-light border text-dark d-flex align-items-center gap-1"
                                        style={{
                                            borderRadius: "8px",
                                            fontFamily: "Outfit, sans-serif",
                                            fontWeight: "600",
                                            borderColor: "rgba(72, 39, 18, 0.15) !important",
                                            backgroundColor: "#fff"
                                        }}
                                        onClick={() => {
                                            setLogTriggerId(activeTrigger.id);
                                            setLogTriggerLabel(activeTrigger.label);
                                            setLogModalOpen(true);
                                        }}
                                        title="Ver histórico de envios"
                                    >
                                        <i className="mdi mdi-history font-size-14 text-muted"></i>
                                        <span>Histórico</span>
                                    </button>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="font-size-12 fw-bold neo-font-outfit" style={{ color: formData.active ? '#2e7d32' : '#784b22' }}>
                                            {formData.active ? 'Ativo' : 'Inativo'}
                                        </span>
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={formData.active}
                                                onChange={() => {
                                                    const newVal = !formData.active;
                                                    handleFieldChange("active", newVal);
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    width: '40px',
                                                    height: '20px',
                                                    marginLeft: 0
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Row>
                                {/* Delay (se o trigger suporta) */}
                                {activeTrigger.supportsDelay && (
                                    <Col md={6}>
                                        <NeoInput
                                            type="number"
                                            label={activeTrigger.delayLabel || "Dias de Atraso"}
                                            min="0"
                                            value={formData.delayDays}
                                            onChange={e => handleFieldChange("delayDays", Number(e.target.value))}
                                            className="mb-3"
                                        />
                                    </Col>
                                )}

                                {/* Horário de envio (se configurável) */}
                                {activeTrigger.supportsScheduledTime && (
                                    <Col md={6}>
                                        <NeoInput
                                            type="time"
                                            label="Horário de Envio"
                                            value={formData.scheduledTime || "08:00"}
                                            onChange={e => handleFieldChange("scheduledTime", e.target.value)}
                                            className="mb-3"
                                        />
                                    </Col>
                                )}
                            </Row>

                            {/* Mensagem */}
                            <NeoInput
                                type="textarea"
                                id="automation-message"
                                label="Mensagem de Envio"
                                rows="6"
                                placeholder="Escreva a mensagem automática..."
                                value={formData.message}
                                onChange={e => handleFieldChange("message", e.target.value)}
                                error={formData.message?.length > 0 && formData.message.length < 10 ? 'Mínimo 10 caracteres' : ''}
                                className="mb-3"
                            />

                            {/* Variáveis */}
                            <div className="mb-4">
                                <small className="text-muted fw-bold d-block mb-2 text-uppercase font-size-10 letter-spacing-1">
                                    Campos Dinâmicos (Clique para inserir na mensagem):
                                </small>
                                <div className="d-flex gap-2 flex-wrap">
                                    {TEMPLATE_VARIABLES.map(v => (
                                        <span
                                            key={v.key}
                                            className="neo-variable-badge"
                                            onClick={() => insertVariable(v.key)}
                                            title={`Insere: ${v.key} — Ex: ${v.example}`}
                                        >
                                            <i className="mdi mdi-plus-circle-outline"></i>
                                            {v.label}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Botão salvar */}
                            <div className="d-flex justify-content-end pt-3 border-top" style={{ borderTop: "1.5px solid rgba(72,39,18,0.10) !important" }}>
                                <NeoButton
                                    variant="primary"
                                    className="px-5 fw-bold shadow-sm"
                                    loading={isSaving}
                                    loadingText="Salvando..."
                                    icon="mdi-content-save-outline"
                                    onClick={handleSubmit}
                                    disabled={!formData.message || formData.message.length < 10}
                                >
                                    Salvar Alterações
                                </NeoButton>
                            </div>
                        </TabPane>
                    )}
                </TabContent>
            </NeoCard>

            {/* Modal de Logs */}
            <AutomationLogModal
                isOpen={logModalOpen}
                toggle={() => setLogModalOpen(false)}
                triggerId={logTriggerId}
                triggerLabel={logTriggerLabel}
            />
        </>
    );
};

export default AutomationDetail;
